import { useState, useEffect, useRef, useCallback } from 'react'
import apiClient from '../services/api'
import { message } from 'antd'

interface SyncResult {
  newEmailsCount: number
  newEmails: any[]
  unreadCount: number
  lastSyncAt: string
}

interface UseRealtimeEmailsResult {
  newEmailCount: number
  unreadCount: number
  isConnected: boolean
  lastSyncAt: Date | null
  manualSync: () => Promise<void>
}

export function useRealtimeEmails(
  onNewEmails?: (emails: any[]) => void,
  isWebSocketConnected: boolean = false
): UseRealtimeEmailsResult {
  const [newEmailCount, setNewEmailCount] = useState(0)
  const [unreadCount, setUnreadCount] = useState(0)
  const [isConnected, setIsConnected] = useState(true)
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null)
  const lastKnownCountRef = useRef<number>(0)
  const hasSyncedRef = useRef<boolean>(false)
  const isSyncingRef = useRef<boolean>(false)
  const onNewEmailsRef = useRef(onNewEmails)

  // Keep the callback ref updated without triggering re-creation of checkForNewEmails
  useEffect(() => {
    onNewEmailsRef.current = onNewEmails
  }, [onNewEmails])

  const checkForNewEmails = useCallback(async (silent: boolean = true) => {
    // Prevent duplicate syncs
    if (isSyncingRef.current) {
      console.log('⏭️ Sync already in progress, skipping...')
      return
    }

    isSyncingRef.current = true
    console.log('🔄 Starting email sync...')

    try {
      const response = await apiClient.post<{ success: boolean; data: SyncResult }>('/gmail/sync')
      
      if (response.data.success) {
        const { newEmailsCount, newEmails, unreadCount: freshUnreadCount, lastSyncAt: syncTime } = response.data.data
        
        setIsConnected(true)
        setUnreadCount(freshUnreadCount)
        setLastSyncAt(new Date(syncTime))
        
        // Check if we have NEW emails since last check
        if (newEmailsCount > 0 && freshUnreadCount > lastKnownCountRef.current) {
          setNewEmailCount(newEmailsCount)
          
          if (!silent) {
            message.success(`${newEmailsCount} new email${newEmailsCount > 1 ? 's' : ''} received!`)
          }
          
          // Call callback with new emails using ref
          if (onNewEmailsRef.current && newEmails.length > 0) {
            onNewEmailsRef.current(newEmails)
          }
        }
        
        // Update last known count
        lastKnownCountRef.current = freshUnreadCount
        
        if (!silent) {
          message.success('Emails synced successfully!')
        }
        
        console.log('✅ Sync complete')
      }
    } catch (error: any) {
      console.error('❌ Failed to check for new emails:', error)
      setIsConnected(false)
      
      if (!silent) {
        message.error('Failed to sync emails')
      }
    } finally {
      isSyncingRef.current = false
    }
  }, []) // Empty deps - stable function

  const manualSync = useCallback(async () => {
    await checkForNewEmails(false)
  }, [checkForNewEmails])

  // Initial sync on mount only (once)
  useEffect(() => {
    if (!hasSyncedRef.current) {
      console.log('📧 Syncing emails on page load...')
      hasSyncedRef.current = true
      checkForNewEmails(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty deps - only run once on mount

  // Auto-polling: Check for new emails every 60 seconds (only if WebSocket disconnected)
  useEffect(() => {
    // Only start polling if WebSocket is NOT connected
    if (isWebSocketConnected) {
      console.log('✅ WebSocket connected - polling disabled')
      return
    }

    console.log('🔄 Starting email polling (60 second interval) - WebSocket disconnected')
    
    const pollInterval = setInterval(() => {
      console.log('⏰ Polling for new emails...')
      checkForNewEmails(true) // Silent check (no toast messages)
    }, 60000) // 60 seconds = 1 minute

    // Cleanup interval on unmount or when WebSocket connects
    return () => {
      console.log('🛑 Stopping email polling')
      clearInterval(pollInterval)
    }
  }, [checkForNewEmails, isWebSocketConnected])

  return {
    newEmailCount,
    unreadCount,
    isConnected,
    lastSyncAt,
    manualSync,
  }
}

